import HTTPService from "./HTTPService";
import type { AddGValuePayload } from "@/types/gValue/gValue";
import type { Account } from "@/types/account/account";
import type { APIResponse } from "@/types/APIresponse";
import type { MainAccount } from "@/types/mainAccount/mainAccount";
import type { VainuCompany } from "@/types/account/vainu_company";
import type {
	CreateDocument,
	Document,
	DocumentCategory,
	DocumentLanguage,
} from "@/types/document/document";
import type {
	AccountTree,
	AccountTreeForAccount,
	CreateAccountTree,
	UpdateAccountTree,
} from "@/types/accountTree/accountTree";
import type { ClientData } from "@/types/client/clientData";
import type { Auth } from "@/types/auth";
import type { Group } from "@/types/group/group";
import type { Insurance } from "@/types/insurance/insurance";
import type { InsuranceProductDefinitionRaw } from "@/types/insurance/productDefinition";
import type { CreateLink } from "@/types/link/link";

class AppService {
	private http: HTTPService;

	constructor() {
		this.http = new HTTPService();
	}

	// ----------------------- Auth data -----------------------

	getAuth = async (): Promise<APIResponse<Auth>> => {
		return await this.http.sendGetRequest("/v1/admin/auth/userinfo");
	};

	// ----------------------- Client data -----------------------
	getClientData = async (
		group_id: string
	): Promise<APIResponse<ClientData>> => {
		return await this.http.sendGetRequest("/v1/client/" + group_id);
	};

	// ----------------------- Dashboard -----------------------

	// Fetch all main accounts
	createMainAccountTree = async (
		data: CreateAccountTree
	): Promise<APIResponse<null>> => {
		const response = await this.http.sendPostRequest(
			"/v1/admin/account-tree/",
			data
		);
		return response;
	};

	getMainAccountTree = async (
		id: string
	): Promise<APIResponse<AccountTree>> => {
		const response = await this.http.sendGetRequest(
			`/v1/admin/account-tree/${id}`
		);
		return response;
	};

	updateMainAccountTree = async (
		id: string,
		data: UpdateAccountTree
	): Promise<APIResponse<null>> => {
		const response = await this.http.sendPutRequest(
			`/v1/admin/account-tree/${id}`,
			data
		);
		return response;
	};

	// ----------------------- SingleMainAccount -----------------------

	// Fetch main account details
	getSingleMainAccount = async (
		mainAccountId: string
	): Promise<APIResponse<MainAccount>> => {
		const response = await this.http.sendGetRequest(
			"/v1/admin/main-account/" + mainAccountId
		);
		return response;
	};

	// Fetch associated accounts to a main account
	getAccounts = async (
		mainAccountId: string
	): Promise<APIResponse<Account[]>> => {
		const response = await this.http.sendGetRequest(
			`/v1/admin/main-account/${mainAccountId}/accounts`
		);
		return response;
	};

	deleteMainAccount = async (
		mainAccountId: string
	): Promise<APIResponse<null>> => {
		const response = await this.http.sendDeleteRequest(
			`/v1/admin/main-account/${mainAccountId}/delete`
		);
		return response;
	};

	// ----------------------- Accounts -----------------------
	getAllAccounts = async (): Promise<APIResponse<Account[]>> => {
		const response = await this.http.sendGetRequest("/v1/admin/accounts/all");
		return response;
	};

	// ----------------------- SingleAccount -----------------------

	// Fetch single account
	getSingleAccount = async (
		accountId: string
	): Promise<APIResponse<Account>> => {
		const response = await this.http.sendGetRequest(
			"/v1/admin/accounts/" + accountId
		);
		return response;
	};

	getSingleAccountFullTree = async (
		accountId: string
	): Promise<APIResponse<AccountTreeForAccount>> => {
		const response = await this.http.sendGetRequest(
			"/v1/admin/account-tree/single/" + accountId
		);
		return response;
	};

	// ----------------------- Groups -----------------------
	// Fetch all groups for an account
	getAccountGroups = async (
		accountId: string
	): Promise<APIResponse<Group[]>> => {
		const response = await this.http.sendGetRequest(
			`/v1/admin/accounts/${accountId}/groups`
		);
		return response;
	};

	// ----------------------- Products -----------------------
	async getProductsByGroup(groupId: string): Promise<APIResponse<Insurance[]>> {
		const response = await this.http.sendGetRequest(
			`/v1/admin/accounts/group/${groupId}/getproducts`
		);
		return response;
	}

	// Catalog of available insurance products and their default parameter values.
	// Used to seed the create-account form and to look up baseline values when
	// detecting whether a parameter has been altered by the user.
	getInsuranceProductDefinitions = async (): Promise<
		APIResponse<InsuranceProductDefinitionRaw[]>
	> => {
		const response = await this.http.sendGetRequest(
			"/v1/admin/insurance-product-definitions"
		);
		return response;
	};

	// ----------------------- Documents -----------------------
	getDocumentsByGroup = async (
		groupId: string
	): Promise<APIResponse<Document[]>> => {
		const response = await this.http.sendGetRequest(
			`/v1/admin/groups/${groupId}/documents`
		);
		return response;
	};

	deleteDocument = async (documentId: string): Promise<APIResponse<void>> => {
		const response = await this.http.sendDeleteRequest(
			`/v1/admin/documents/${documentId}/delete`
		);
		return response;
	};

	updateDocumentLanguage = async (
		documentId: string,
		language: DocumentLanguage | null
	): Promise<APIResponse<Document>> => {
		const response = await this.http.sendPutRequest(
			`/v1/admin/documents/${documentId}/language`,
			{ language }
		);
		return response;
	};

	getDocumentBySasToken = async (
		documentId: string
	): Promise<APIResponse<string>> => {
		const response = await this.http.sendGetRequest(
			`/v1/documents/${documentId}/download`
		);
		return response;
	};

	// ! Not in use
	// async uploadDocumentForGroup(
	// 	groupId: string,
	// 	category: DocumentCategory,
	// 	file: File,
	// 	filename: string
	// ): Promise<null> {
	// 	const formData = new FormData();
	// 	formData.append("file", file, filename);
	// 	formData.append("category", category);
	// 	formData.append("name", filename);

	// 	const url = `/v1/admin/groups/${groupId}/documents`;
	// 	await this.http.sendFormDataPostRequest(url, formData);

	// 	return null;
	// }

	async uploadMultipleDocuments(
		accountId: string,
		groupIds: string[],
		category: DocumentCategory,
		language: DocumentLanguage,
		docs: CreateDocument[]
	): Promise<unknown> {
		const formData = new FormData();
		formData.append("category", category);
		formData.append("language", language);
		for (const id of groupIds) {
			formData.append("group_ids[]", id);
		}
		for (const doc of docs) {
			formData.append("files[]", doc.file, doc.name);
		}

		const url = `/v1/admin/documents/${accountId}`;
		return await this.http.sendFormDataPostRequest(url, formData);
	}

	// ------- Links -------
	async getLinksByGroup(groupId: string): Promise<APIResponse<Document[]>> {
		const url = `/v1/admin/groups/${groupId}/links`;
		const response = await this.http.sendGetRequest(url);
		return response;
	}

	async deleteLink(linkId: string): Promise<void> {
		const url = `/v1/admin/links/${linkId}/delete`;
		await this.http.sendDeleteRequest(url);
	}

	async crateLink(account_id: string, groupIds: string[], links: CreateLink[]) {
		return await this.http.sendPostRequest(
			`/v1/admin/links/${account_id}/create`,
			links.map((link) => ({
				name: link.name,
				url: link.url,
				groups: groupIds,
			}))
		);
	}

	// ----------------------- GValue -----------------------
	getValues = async () => {
		const response = await this.http.sendGetRequest("/v1/admin/gvalues/");
		return response;
	};

	addValue = async (data: AddGValuePayload) => {
		const response = await this.http.sendPostRequest(
			"/v1/admin/gvalues/addvalue",
			data
		);
		return response;
	};

	deleteValue = async (id: string): Promise<APIResponse<null>> => {
		const url = `/v1/admin/gvalues/deletevalue/${id}`;
		const response = await this.http.sendDeleteRequest(url);
		return response;
	};

	// ----------------------- Search company -----------------------
	searchCompany = async (
		name_starts_with: string
	): Promise<APIResponse<VainuCompany[]>> => {
		return await this.http.sendGetRequest(
			"/v1/admin/accounts/search?name=" + name_starts_with
		);
	};
}
export default AppService;
